import { DataTransmissionMode } from '@nibras/core/common';
import { ComponentParameter } from '../app.model';
import { BaseCoreObject } from '../base-core-object.model';
import { dcAttributeFilterTVP } from '../dcattribute-filter-tvp.model';
import { dcAttribute } from '../dcattribute.model';
import { dcAttributeDataKey } from '../dcAttributeDataKey.model';
import { dcOverriddenQueryParams } from '../dcOverriddenQueryParams.model';
import { dcTag } from '../dcTag.model';
import { dcValue } from '../dcvalue.model';
import { Dictionary } from '../dictionary';
import { GenericValue } from '../generic-value.model';
import { ComponentData } from '../response/component-data.model';
import { DataMethods } from '../response/data-method.enum';
import { dcSuggestedValue } from '../suggested-value.model';

export class BaseRequest extends BaseCoreObject {
  //public TimeStamp: Date;
  public RequestID: string;
  public UserSessionID: string;
  public ValidateDatastoreAndSend(): void {
    return;
  }
}

export class DataRequest extends BaseRequest {
  public RequestType: RequestType;
  public AttributeFilter: Array<dcAttributeFilterTVP>;
  public Attributes: Array<dcAttribute>;
  public RootElementVersionID: number;
  public ElementVersionID: number;
  public ActivityInstanceID: number;
  public ProcessInstanceID: number;
  public ParentProcessInstanceID?: number;
  public ActionElementVersionID: number;
  public ExternalParameter: dcExternalParameter;
  public ExternalParameters: Array<dcExternalParameter>;
  public AttributeDataKeys: dcAttributeDataKey[];
  public DataKeyWithAttribute: boolean;
  public ComponentParameters: ComponentParameter[];
  public DataTransmissionMode: DataTransmissionMode;
  public queryOperation: QueryOperation;
  public Data: Dictionary<dcValue[]>;
  public Values: Dictionary<dcValue[]>;
  public OverriddenQueryParams: Array<dcOverriddenQueryParams>;

  constructor(
    requestID: string,
    tags: dcTag[],
    requestType: RequestType,
    attributeFilters: Array<dcAttributeFilterTVP> = [],
    externalParameter?: dcExternalParameter,
    rootElementVersionID?: number,
    elementVersionID?: number,
    componentParameters: ComponentParameter[] = [],
    activityInstanceID?: number,
    processInstanceID?: number,
    parentProcessInstanceID?: number,
    actionElementVersionID?: number,
    queryOperation?: QueryOperation,
    overriddenQueryParams: Array<dcOverriddenQueryParams> = []
  ) {
    super();
    this.RequestID = requestID;
    this.Tags = tags;
    this.RequestType = requestType;
    this.AttributeFilter = attributeFilters;
    this.ExternalParameter = externalParameter;
    this.RootElementVersionID = rootElementVersionID;
    this.ElementVersionID = elementVersionID;
    this.ComponentParameters = componentParameters;
    this.ActivityInstanceID = activityInstanceID;
    this.ProcessInstanceID = processInstanceID;
    this.ActionElementVersionID = actionElementVersionID;
    this.queryOperation = queryOperation || QueryOperation.READ;
    this.ParentProcessInstanceID = parentProcessInstanceID;
    this.OverriddenQueryParams = overriddenQueryParams;
  }
}

export type AttributesCallback = (attributeList: dcAttribute[]) => void;

export type KeysCallback = (attributeDataKeyList: dcAttributeDataKey[]) => void;

export type ValuesCallback = (
  attributeList: dcAttribute[],
  attributeDataKeyList: dcAttributeDataKey[],
  attributeValueList: Dictionary<dcValue[]>,
  isRefreshResponse?: boolean
) => void;

// eslint-disable-next-line @typescript-eslint/ban-types
export type GenericObjectValue = GenericValue | object;

export type OnConsolidatedDataReceivedCallback = (
  componentData: ComponentData[],
  attributeDataKeyList: dcAttributeDataKey[]
) => void;

export type LookupDataCallback = (
  attributeDataKeyList: dcAttributeDataKey[],
  suggestedValues: Dictionary<dcSuggestedValue[]>
) => void;

export type onComponentDataAsJSONReceivedCallback = (
  jsonData: Dictionary<GenericValue>[],
  attributeDataKeyList: dcAttributeDataKey[]
) => void;

// TODO: Should need to make more generic with
export class AsyncRequestParams {
  tags: dcTag[];
  externalParameter: dcExternalParameter;
  rootElementVersionID: number;
  elementVersionID: number;
  attributes: dcAttribute[];
  attributeFilter: dcAttributeFilterTVP[];
  thisArg: any;
  attributeDataKeyList: dcAttributeDataKey[];
  componentParameter: ComponentParameter[];
  dataTransmissionMode: DataTransmissionMode;
  isChildAttributesRequired: boolean;
  processInstanceID: number;
  activityInstanceID: number;
  parentProcessInstanceID?: number;
  processAttributesCallback: AttributesCallback;
  processKeysCallback: KeysCallback;
  processValuesCallback: ValuesCallback;
  processOnConsolidatedDataReceivedCallback: OnConsolidatedDataReceivedCallback;
  processonComponentDataAsJSONReceivedCallback: onComponentDataAsJSONReceivedCallback;
  lookupDataReceivedCallback: LookupDataCallback;
  constructor(_thisArg: any = null) {
    this.tags = [new dcTag()];
    this.attributes = [];
    this.attributeFilter = [];
    this.attributeDataKeyList = [];
    this.componentParameter = [];
    this.isChildAttributesRequired = false;
    this.processInstanceID = null;
    this.activityInstanceID = null;
    this.dataTransmissionMode = null;
    if (_thisArg) {
      this.thisArg = _thisArg;
      this.processAttributesCallback = _thisArg.onAttributesReceived;
      this.processKeysCallback = _thisArg.onKeysReceived;
      this.processValuesCallback = _thisArg.onDataReceived;
      this.processOnConsolidatedDataReceivedCallback = _thisArg.onConsolidatedDataReceived;
      this.processonComponentDataAsJSONReceivedCallback = _thisArg.onComponentDataAsJSONReceived;
      this.lookupDataReceivedCallback = _thisArg.onLookupDataRecived;
    }
  }
}

export enum RequestType {
  Element = 1,
  Attribute = 2,
  AttributeValue = 4,
  AttributeAndValuesAsync = 8,
  AttributeTemplate = 16,
  ElementTemplate = 32,
  App = 64,
  ProcessInstance = 128,
  DataReference = 256,
  AdhocTrending = 512
}

export enum QueryOperation {
  READ = 1,
  WRITE = 2,
  PERSISTENTREAD = 3,
  PERSISTENTWRITE = 4,
  WRITETOSOURCE = 5,
  LOOKUP = 6
}

export enum MetaDataLabels {
  RootElementName = 1,
  ElementName = 2,
  RootElementTemplateName = 4,
  ElementPath = 8,
  AttributePath = 16,
  ElementTypeID = 32,
  RootElementVersionID = 64,
  ElementVersionID = 128,
  AttributeID = 256,
  Value = 512,
  ValueTime = 1024,
  IsGood = 2048
}

export class dcExternalParameter {
  public DataMethod: DataMethods;
  public StartTime?: Date;
  public EndTime?: Date;
  public Intervals = 200;
  public AggregateDataMethod: AggregateDataMethods;
  public UniqueID: string;
  public LastGoodValue: string;
  public FilterExpression: string;
  public IncludeFilterValues: boolean;
  public FunctionName: string;
  public GoodPercentage?: number;
  public StartTimeString: string;
  public EndTimeString: string;
  public RetrievalMode: RetrievalMode;
  public BoundaryType: BoundaryType;
  public Count: number;
  public NoOfValues: number;
  public times: Array<Date>;
  public GroupDataComponentId: number[];
  public Proximity?: number;
  public DataFrequency?: string;
  public ProcessStatus?: number;
  public PowerBIReport: boolean;
  constructor(config?: {
    DataMethod?: DataMethods;
    StartTime?: Date;
    EndTime?: Date;
    Intervals?: number;
    Proximity?: number;
    AggregateDataMethod?: number;
    DataFrequency?: string;
    ProcessStatus?: number;
  }) {
    if (config) {
      this.DataMethod = config.DataMethod;
      this.StartTime = config.StartTime ? new Date(config.StartTime) : null;
      this.EndTime = config.EndTime ? new Date(config.EndTime) : null;
      this.Intervals = config.Intervals;
      this.Proximity = config.Proximity;
      this.AggregateDataMethod = config.AggregateDataMethod;
      this.DataFrequency = config.DataFrequency;
      this.ProcessStatus = config.ProcessStatus ? config.ProcessStatus : null;
    }
  }
}

export enum AggregateDataMethods {
  None = 0,
  // eslint-disable-next-line no-bitwise
  Average = 1 << 0,
  // eslint-disable-next-line no-bitwise
  Sum = 2 << 1,
  // eslint-disable-next-line no-bitwise
  Min = 4 << 2,
  // eslint-disable-next-line no-bitwise
  Max = 8 << 4,
  // eslint-disable-next-line no-bitwise
  FlatLine = 16 << 8
}

export enum RetrievalMode {
  Auto = 0,
  AtOrBefore = 1,
  AtOrAfter = 2,
  Exact = 4,
  Before = 8,
  After = 16
}

export enum BoundaryType {
  Inside = 0,
  Outside = 1,
  Interpolated = 2
}
